import { supabase } from '@/integrations/supabase/client';
import { getUserGymId } from '@/utils/gymAuth';

/**
 * Centralized bookings service
 * All booking operations go through edge functions
 *
 * ⚠️ Contrat : ces lectures LÈVENT en cas d'échec, elles ne renvoient jamais
 * une liste vide « par défaut ». Renvoyer `[]` sur une panne réseau faisait
 * afficher « Aucune réservation à venir, réserve vite ! » et neutralisait le
 * garde-fou « déjà réservé » (double réservation possible) — cf. audit lot 7.
 * Un tableau vide signifie donc toujours « le serveur a répondu : rien ».
 */

export interface Booking {
  id: string;
  classId: string;
  className: string;
  date: string;
  time: string;
  duration: string;
  coach: string;
  status: string;
  userId: string;
  capacity?: number;
  category?: string;
  attendance?: {
    id: string;
    attended: boolean;
    rating: number | null;
    notes: string | null;
  } | null;
}

export interface BookingAttendee {
  bookingId: string;
  userId: string;
  firstName: string;
  lastName: string;
  status: string;
  attended: boolean;
  attendanceId: string | null;
  rating: number | null;
  notes: string | null;
}

interface GetUserBookingsParams {
  userId?: string;
  status?: string;
  dateFilter?: 'upcoming' | 'past';
}

interface ConfirmAttendanceParams {
  classId: string;
  classDate: string;
  attendances: Array<{
    bookingId: string;
    attended: boolean;
    rating?: number;
    notes?: string;
  }>;
}

/**
 * Get bookings for a user
 */
export async function getUserBookings(params: GetUserBookingsParams = {}): Promise<Booking[]> {
  const gymId = getUserGymId();
  if (!gymId) {
    console.error('[bookingsService] No gym_id available');
    throw new Error('No gym_id available');
  }

  const { data, error } = await supabase.functions.invoke('bookings-management', {
    body: {
      action: 'get_user_bookings',
      gym_id: gymId,
      ...params
    }
  });

  if (error) {
    console.error('[bookingsService] Error fetching user bookings:', error);
    throw error;
  }

  return data?.bookings || [];
}

/**
 * Get attendees for a specific class
 */
export async function getBookingAttendees(classId: string, classDate: string): Promise<BookingAttendee[]> {
  const gymId = getUserGymId();
  if (!gymId) {
    console.error('[bookingsService] No gym_id available');
    throw new Error('No gym_id available');
  }

  const { data, error } = await supabase.functions.invoke('bookings-management', {
    body: {
      action: 'get_booking_attendees',
      gym_id: gymId,
      classId,
      classDate
    }
  });

  if (error) {
    console.error('[bookingsService] Error fetching attendees:', error);
    throw error;
  }

  return data?.attendees || [];
}

/**
 * Get raw bookings for a specific class (no role filtering)
 */
export async function getBookingsForClass(
  classId: string, 
  classDate: string
): Promise<{ bookingId: string; userId: string; status: string }[]> {
  const gymId = getUserGymId();
  if (!gymId) {
    console.error('[bookingsService] No gym_id available');
    throw new Error('No gym_id available');
  }

  const { data, error } = await supabase.functions.invoke('bookings-management', {
    body: {
      action: 'get_bookings_for_class',
      gym_id: gymId,
      classId: classId,
      date: classDate
    }
  });

  if (error) {
    console.error('[bookingsService] Error fetching bookings:', error);
    throw error;
  }

  return (data?.bookings || []).map((b: any) => ({
    bookingId: b.id,
    userId: b.user_id,
    status: b.status
  }));
}

/**
 * Get all bookings for a specific date (filtered by role='client')
 */
export async function getDailyBookings(
  date: string
): Promise<{ 
  bookingId: string; 
  userId: string; 
  classId: string; 
  status: string;
  time: string;
  duration: string;
}[]> {
  const gymId = getUserGymId();
  if (!gymId) {
    console.error('[bookingsService] No gym_id available');
    throw new Error('No gym_id available');
  }

  const { data, error } = await supabase.functions.invoke('bookings-management', {
    body: {
      action: 'get_daily_bookings',
      gym_id: gymId,
      date: date
    }
  });

  if (error) {
    console.error('[bookingsService] Error fetching daily bookings:', error);
    throw error;
  }

  return (data?.bookings || []).map((b: any) => ({
    bookingId: b.id,
    userId: b.user_id,
    classId: b.class_id,
    status: b.status,
    time: b.time,
    duration: b.duration
  }));
}

/**
 * Confirm attendance for multiple bookings
 */
export async function confirmBookingAttendees(params: ConfirmAttendanceParams): Promise<any> {
  try {
    const gymId = getUserGymId();
    if (!gymId) {
      throw new Error('No gym_id available');
    }

    const { data, error } = await supabase.functions.invoke('bookings-management', {
      body: {
        action: 'confirm_booking_attendees',
        gym_id: gymId,
        ...params
      }
    });

    if (error) {
      console.error('[bookingsService] Error confirming attendances:', error);
      throw error;
    }

    return data.results || [];
  } catch (error) {
    console.error('[bookingsService] Unexpected error in confirmBookingAttendees:', error);
    throw error;
  }
}

/**
 * Delete a booking
 */
export async function deleteBooking(bookingId: string): Promise<void> {
  try {
    const gymId = getUserGymId();
    if (!gymId) {
      throw new Error('No gym_id available');
    }

    const { error } = await supabase.functions.invoke('bookings-management', {
      body: {
        action: 'delete_booking',
        gym_id: gymId,
        booking_id: bookingId
      }
    });

    if (error) {
      console.error('[bookingsService] Error deleting booking:', error);
      throw error;
    }
  } catch (error) {
    console.error('[bookingsService] Unexpected error in deleteBooking:', error);
    throw error;
  }
}
